import React, { useEffect, useState } from 'react'
import moment from 'moment'
import 'moment/locale/ro'
import ConfirmationModal from './ConfirmationModal'
import { UserViewModel, formatScheduleHour, CreateConsumerReservationRequestBody } from '@meniudigital/shared'
import { getDayIndex, isDuringOpenHours, isHourBetween, wait } from './helpers'
import RoundButton from './RoundButton'

moment.locale('ro', {
  week: {
    dow: 1, // Start week on Monday
  },
})

const apiUrlElement = document.getElementById('apiUrl') as HTMLInputElement | null
const API_URL = apiUrlElement?.value

function App() {
  const hourInputRef = React.createRef<HTMLInputElement>()
  const minuteInputRef = React.createRef<HTMLInputElement>()

  useEffect(() => {
    fetchRestaurantData()
  }, [])

  const [reservation, setReservation] = useState<CreateConsumerReservationRequestBody>({
    targetUserId: '',
    numberOfPeople: 2,
    requestedDateTime: moment().add(1, 'day').hour(20).minute(0).toDate(),
    extraComments: '',
    phoneNumber: '',
    fullName: '',
    emailAddress: '',
  })

  const [restaurant, setRestaurant] = useState<UserViewModel>()

  const fetchRestaurantData = async () => {
    const username = window.location.pathname.split('/').filter(Boolean)[0]

    const url = `${API_URL}/users/${username}`

    const response = await fetch(url)
    const data = await response.json()

    setRestaurant(data)
  }

  const { requestedDateTime, numberOfPeople, extraComments, phoneNumber, fullName, emailAddress } = reservation

  const [isConfirmationModalVisible, setIsConfirmationModalVisible] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [isShowingErrorMessage, setIsShowingErrorMessage] = useState(false)

  if (!restaurant) {
    return null
  }

  const { schedule = [] } = restaurant.schedule || {}

  // These simply don't work if put outisde the component
  const emailRegex = /\S+@\S+\.\S+/
  const phoneRegex = /^\+?\d{1,4}?[-.\s]?\(?\d{1,3}?\)?[-.\s]?\d{1,4}[-.\s]?\d{1,4}[-.\s]?\d{1,9}$/g

  const isEmailAddressValid = emailRegex.test(emailAddress)
  const isPhoneNumberValid = phoneRegex.test(phoneNumber)

  const isFormValid = isEmailAddressValid && isPhoneNumberValid && fullName.length > 0

  const formattedHours = requestedDateTime.getHours().toString().padStart(2, '0')
  const formattedMinutes = requestedDateTime.getMinutes().toString().padStart(2, '0')

  const targetDaySchedule = schedule.find(day => day.dayIndex === getDayIndex(requestedDateTime))

  const formattedDate = moment(requestedDateTime).calendar(null, {
    sameDay: '[Azi], D MMMM',
    nextDay: '[Mâine], D MMMM',
    nextWeek: 'dddd, D MMMM',
    sameElse: 'dddd, D MMMM',
  })

  const displayDay = moment(requestedDateTime).calendar(null, {
    sameDay: '[azi]',
    nextDay: '[mâine]',
    nextWeek: 'dddd',
    sameElse: 'dddd',
  })

  const openingHour = targetDaySchedule?.openHour || 0
  const closingHour = targetDaySchedule?.closeHour || 24

  const createReservation = async () => {
    const isDuringOpenHours = isHourBetween(requestedDateTime.getHours(), openingHour, closingHour)

    if (targetDaySchedule?.closedAllDay || !isFormValid || !isDuringOpenHours) {
      setIsShowingErrorMessage(true)
      await wait(5000)
      setIsShowingErrorMessage(false)
      return
    }

    setIsLoading(true)
    const response = await fetch(`${API_URL}/consumer-reservations`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ ...reservation, targetUserId: restaurant?.id }),
    })

    setIsLoading(false)

    try {
      const json = await response.json()

      console.log('json', json)

      if (!json.id) {
        alert('A apărut o eroare la trimiterea rezervării. Te rog încearcă din nou.')
      } else {
        setIsConfirmationModalVisible(true)
      }
    } catch (e) {
      alert('A apărut o eroare la trimiterea rezervării. Te rog încearcă din nou.')
    }
  }

  function updateReservationDetail(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, infoType: string) {
    const newValue = e.target.value

    switch (infoType) {
      case 'hours': {
        const newHour = Math.min(Number(newValue), 23)

        setReservation({
          ...reservation,
          requestedDateTime: moment(requestedDateTime).hour(newHour).toDate(),
        })
        if (newHour > 2) {
          minuteInputRef.current?.select()
        }
        break
      }
      case 'minutes': {
        const newMinutes = Math.min(Number(newValue), 59)

        setReservation({
          ...reservation,
          requestedDateTime: moment(requestedDateTime).minutes(newMinutes).toDate(),
        })
        if (newMinutes > 5) {
          minuteInputRef.current?.blur()
        }
        break
      }
      case 'name': {
        setReservation({ ...reservation, fullName: newValue })
        break
      }
      case 'email': {
        setReservation({ ...reservation, emailAddress: newValue })
        break
      }
      case 'phonenumber': {
        setReservation({ ...reservation, phoneNumber: newValue })
        break
      }
      case 'extracomments': {
        setReservation({ ...reservation, extraComments: newValue })
      }
    }
  }

  const changeDate = (changeType: string) => {
    if (changeType === 'increment') {
      const newDate = requestedDateTime
      newDate.setDate(requestedDateTime.getDate() + 1)
      setReservation({ ...reservation, requestedDateTime: newDate })
    }
    if (changeType === 'decrement') {
      const newDate = requestedDateTime
      newDate.setDate(requestedDateTime.getDate() - 1)
      setReservation({ ...reservation, requestedDateTime: newDate })
    }
  }

  return (
    <div className="wrapper">
      {isConfirmationModalVisible && <ConfirmationModal {...reservation} />}

      <div className="pickers-wrapper">
        <h1>Rezervă o masă la {restaurant.name}</h1>

        <span className="question">Câte persoane?</span>
        <div className="numeric-picker">
          <RoundButton
            iconName="minus"
            onClick={() => setReservation({ ...reservation, numberOfPeople: numberOfPeople - 1 })}
            disabled={numberOfPeople <= 1}
          />
          <div className="date-wrapper">
            <span className="numeric">{numberOfPeople}</span>
          </div>
          <RoundButton
            iconName="plus"
            onClick={() => setReservation({ ...reservation, numberOfPeople: numberOfPeople + 1 })}
            disabled={numberOfPeople >= 20}
          />
        </div>
        <span className="question">Când?</span>
        <div className="numeric-picker">
          <RoundButton
            iconName="chevron-left"
            onClick={() => changeDate('decrement')}
            disabled={moment(requestedDateTime).isSame(moment(), 'day')}
          />
          <div className="date-wrapper">
            <span className="date">{formattedDate}</span>
          </div>
          <RoundButton iconName="chevron-right" onClick={() => changeDate('increment')} />
        </div>
        <span className="question">La ce oră?</span>
        <div className="numeric-picker">
          <input
            ref={hourInputRef}
            type="number"
            className="left"
            maxLength={2}
            max={23}
            value={formattedHours}
            onFocus={() => hourInputRef.current?.select()}
            onChange={value => updateReservationDetail(value, 'hours')}
          />
          <input
            ref={minuteInputRef}
            type="number"
            className="right"
            maxLength={2}
            max={59}
            value={formattedMinutes}
            onFocus={() => minuteInputRef.current?.select()}
            onChange={value => updateReservationDetail(value, 'minutes')}
          />
        </div>
        {!isDuringOpenHours(requestedDateTime, openingHour, closingHour) && (
          <span className="hour-error">Restaurantul e închis la ora solicitată.</span>
        )}
        {targetDaySchedule?.closedAllDay ? (
          <span className="opening-hours">Restaurantul e închis {displayDay}.</span>
        ) : (
          <span className="opening-hours">
            Restaurantul e deschis {displayDay} între {formatScheduleHour(openingHour)}
            {' și '}
            {formatScheduleHour(closingHour)}.
          </span>
        )}
      </div>
      <div className="angled-separator"></div>
      <div className="info-wrapper">
        <div className="info">
          <label>Nume complet</label>
          <input
            maxLength={100}
            placeholder="e.g. Popescu Ioan"
            value={fullName}
            onChange={value => updateReservationDetail(value, 'name')}
          />
        </div>
        <div className={`info ${!isEmailAddressValid && emailAddress.length > 4 ? 'invalid' : ''}`}>
          <label>Adresă de e-mail</label>
          <input
            type="email"
            maxLength={70}
            placeholder="e.g. popescu.ioan@gmail.com"
            value={emailAddress}
            onChange={value => updateReservationDetail(value, 'email')}
          />
        </div>
        <div className={`info ${!isPhoneNumberValid && phoneNumber?.length > 4 ? 'invalid' : ''}`}>
          <label>Număr de telefon</label>
          <input
            type="tel"
            maxLength={50}
            placeholder="e.g. +40726213476"
            value={phoneNumber}
            onChange={value => updateReservationDetail(value, 'phonenumber')}
          />
        </div>
        <div className="info">
          <label>Mențiuni</label>
          <textarea
            maxLength={100}
            placeholder="e.g. Aș dori o masă pe terasă dacă este disponibilă, mulțumesc"
            value={extraComments}
            onChange={value => updateReservationDetail(value, 'extracomments')}
          />
        </div>
        <p style={{ margin: '0', marginTop: -8, fontSize: 14, opacity: isShowingErrorMessage ? 1 : 0 }}>
          Asigură-te că ai completat corect toate câmpurile și încearcă din nou.
        </p>

        <div className={`submit-button ${isLoading ? 'loading' : ''}`} onClick={createReservation}>
          {isLoading ? 'Se trimite...' : 'Trimite rezervarea'}
        </div>
      </div>
    </div>
  )
}

export default App
